import React, { useState, useEffect } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

interface ApplicationSettingsData {
  showCoachableSubscriptionPlans: boolean;
  additionalCreditGainPercentage: number;
  showUserEarnings: boolean;
  aboutUs: string;
  privacyPolicy: string;
  termsAndConditions: string;
  frequentlyAskedQuestions: string;
}

export function ApplicationSettings() {
  const [currentView, setCurrentView] = useState<"list" | "edit">("edit");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<ApplicationSettingsData>({
    showCoachableSubscriptionPlans: true,
    additionalCreditGainPercentage: 5,
    showUserEarnings: true,
    aboutUs: `WHO WE ARE
We take on today's biggest problems because we believe a better world is possible.

As a corporate entity, Able® creates business platforms for professionals through our business professionals to digitally provide services to consumers using virtual analysis technology. Able develops the platforms, seek out professionals, and create a digital society to fit the needs of the masses within the industry.

WHAT MAKES US DIFFERENT
We represent the Modern Opportunist.`,
    privacyPolicy: `Coaches, teachers, musicians, do it yourself professionals and healthcare practitioners are among some of the professionals our company seeks to eventually provide with this technology service.

WHAT WE BELIEVE
We are the path to making people able.

Our business professionals and consumers are passionate, ambitious and full of potential. But they face roadblocks to their progress . They need the space and opportunity to rise. With a platform where change can happen, possibilities are endless, potential is realized and growth is inevitable.`,
    termsAndConditions: `Terms of Use

Coachable

— Terms and Conditions of Use —

Welcome to Coachable, which is provided by able Inc. in the United States.`,
    frequentlyAskedQuestions: ""
  });

  // Rich text editor component
  const RichTextEditor = ({ 
    value, 
    onChange, 
    placeholder = "Enter content...",
    minHeight = "200px"
  }: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minHeight?: string;
  }) => (
    <div className="border rounded-lg">
      {/* Rich Text Editor Toolbar */}
      <div className="border-b p-2 flex flex-wrap items-center gap-1 bg-muted/50">
        <Button variant="ghost" size="sm" type="button" title="Bold">
          <span className="font-bold text-sm">B</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Italic">
          <span className="italic text-sm">I</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Underline">
          <span className="underline text-sm">U</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Strikethrough">
          <span className="line-through text-sm">S</span>
        </Button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <Button variant="ghost" size="sm" type="button" title="Bullet List">
          <span className="text-sm">•</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Numbered List">
          <span className="text-sm">1.</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Decrease Indent">
          <span className="text-sm">←</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Increase Indent">
          <span className="text-sm">→</span>
        </Button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <Button variant="ghost" size="sm" type="button" title="Link">
          <span className="text-sm">🔗</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Image">
          <span className="text-sm">🖼️</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Table">
          <span className="text-sm">⊞</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Horizontal Rule">
          <span className="text-sm">—</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Special Characters">
          <span className="text-sm">Ω</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Blockquote">
          <span className="text-sm">"</span>
        </Button>
        <Button variant="ghost" size="sm" type="button" title="Source">
          <span className="text-sm">Source</span>
        </Button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <Select defaultValue="normal">
          <SelectTrigger className="w-20 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">Styles</SelectItem>
            <SelectItem value="heading1">H1</SelectItem>
            <SelectItem value="heading2">H2</SelectItem>
            <SelectItem value="heading3">H3</SelectItem>
          </SelectContent>
        </Select>
        
        <Select defaultValue="paragraph">
          <SelectTrigger className="w-20 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph">Format</SelectItem>
            <SelectItem value="div">Div</SelectItem>
            <SelectItem value="pre">Pre</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="ghost" size="sm" type="button" title="Help">
          <span className="text-sm">?</span>
        </Button>
      </div>
      
      {/* Text Area */}
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-0 resize-none focus-visible:ring-0 rounded-t-none"
        style={{ minHeight }}
      />
    </div>
  );

  // Simulate API call to fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        // Simulate API call - replace with real API endpoint
        // const response = await fetch("host/api/v1/application-settings", {
        //   headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        // });
        
        // Mock data for demonstration
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Settings are already set in state with mock data
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call to save settings
      // const response = await fetch("host/api/v1/application-settings", {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify(settings)
      // });

      console.log("Saving application settings:", settings);
      
      // Mock success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // You could show a success toast here
    } catch (error) {
      console.error("Failed to save settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1>Edit</h1>
          <p className="text-muted-foreground">
            Configure application settings and content
          </p>
        </div>
      </div>

      {/* Settings Form */}
      <div className="space-y-6">
        {/* Show Coachable Subscription Plans */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showCoachableSubscriptionPlans"
                checked={settings.showCoachableSubscriptionPlans}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showCoachableSubscriptionPlans: !!checked }))
                }
              />
              <Label htmlFor="showCoachableSubscriptionPlans">
                Show Coachable Subscription Plans
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Additional Credit Gain Percentage */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Label htmlFor="additionalCreditGain">
              Additional Credit Gain Percentage On Web Signup
            </Label>
            <Input
              id="additionalCreditGain"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.additionalCreditGainPercentage}
              onChange={(e) => 
                setSettings(prev => ({ 
                  ...prev, 
                  additionalCreditGainPercentage: parseFloat(e.target.value) || 0 
                }))
              }
              className="max-w-xs"
            />
          </CardContent>
        </Card>

        {/* Show User Earnings */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showUserEarnings"
                checked={settings.showUserEarnings}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showUserEarnings: !!checked }))
                }
              />
              <Label htmlFor="showUserEarnings">
                Show User Earnings
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* About Us */}
        <Card>
          <CardHeader>
            <CardTitle>About Us</CardTitle>
            <CardDescription>
              Edit the About Us content displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              value={settings.aboutUs}
              onChange={(value) => setSettings(prev => ({ ...prev, aboutUs: value }))}
              placeholder="Enter About Us content..."
              minHeight="300px"
            />
          </CardContent>
        </Card>

        {/* Privacy Policy */}
        <Card>
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
            <CardDescription>
              Edit the Privacy Policy content displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              value={settings.privacyPolicy}
              onChange={(value) => setSettings(prev => ({ ...prev, privacyPolicy: value }))}
              placeholder="Enter Privacy Policy content..."
              minHeight="300px"
            />
          </CardContent>
        </Card>

        {/* Terms And Conditions */}
        <Card>
          <CardHeader>
            <CardTitle>Terms And Conditions</CardTitle>
            <CardDescription>
              Edit the Terms and Conditions content displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              value={settings.termsAndConditions}
              onChange={(value) => setSettings(prev => ({ ...prev, termsAndConditions: value }))}
              placeholder="Enter Terms and Conditions content..."
              minHeight="300px"
            />
          </CardContent>
        </Card>

        {/* Frequently Asked Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
            <CardDescription>
              Edit the FAQ content displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              value={settings.frequentlyAskedQuestions}
              onChange={(value) => setSettings(prev => ({ ...prev, frequentlyAskedQuestions: value }))}
              placeholder="Enter FAQ content..."
              minHeight="300px"
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="min-w-[100px]"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}